import React, { useEffect } from 'react';
import setSeo from '../../utils/seo.js';
import BackToTop from '../../components/BackToTop.jsx';
import case1 from '../../assets/case1.webp';

/**
 * Individual case study: Healthcare Industry – AI-Powered Appointment Scheduling System
 * Full case study page with detailed implementation and results
 */
const HealthcareScheduling = () => {
  useEffect(() => {
    setSeo({
      title: 'Healthcare AI Scheduling Case Study - 70% Fewer Conflicts | Aakeeo',
      description:
        'See how our AI-powered appointment scheduling system reduced scheduling conflicts by 70% and cut confirmation times from hours to under 1 minute for a multi-specialty healthcare provider.',
      keywords: ['healthcare automation', 'AI scheduling', 'appointment automation', 'healthcare case study', 'medical scheduling software', 'AI healthcare solutions'],
      canonical: window.location.href,
      og: {
        type: 'article',
        image: 'https://aakeeo.com' + case1
      },
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': 'Healthcare Industry – AI-Powered Appointment Scheduling System',
        'description': 'An AI-driven scheduling agent that reduced scheduling conflicts by 70% and appointment confirmations from hours to under 1 minute.',
        'image': 'https://aakeeo.com' + case1,
        'author': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies'
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://aakeeo.com/Aakeeo_favicon.png'
          }
        }
      }
    });
  }, []);
  return (
    <div className="case-study-page">
      <section className="page-hero case-studies-hero">
        <video className="page-hero-video" autoPlay muted loop>
          <source src="/background.mp4" type="video/mp4" />
        </video>
        <div className="page-hero-content">
          <h1 className="page-hero-title">Healthcare Industry – AI-Powered Appointment Scheduling System</h1>
          <p className="page-hero-subtitle">An AI-driven scheduling agent that reduced scheduling conflicts by 70% and appointment confirmations from hours to under 1 minute.</p>
        </div>
      </section>

      <article className="section reveal">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 2rem' }}>
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <img
              src={case1}
              alt="Healthcare Scheduling Success Story"
              style={{
                width: '100%',
                maxWidth: '600px',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}
            />
          </div>

          <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-light)' }}>
            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Challenge
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              A multi-specialty healthcare provider faced inefficiencies in manual appointment scheduling. Patients often requested specific doctors or time slots, but front desk staff had to check availability manually across calendars. This led to:
            </p>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>High scheduling conflicts and reschedules</li>
              <li style={{ marginBottom: '0.5rem' }}>Delays in patient confirmation</li>
              <li style={{ marginBottom: '0.5rem' }}>Limited visibility for both doctors and patients</li>
            </ul>

            <p style={{ marginBottom: '1.5rem' }}>
              The client needed a real-time, intelligent scheduling system integrated with their website that could automate the process while ensuring minimal errors.
            </p>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Requirements
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Real-time doctor schedule lookup</li>
              <li style={{ marginBottom: '0.5rem' }}>Intelligent conflict resolution with alternative suggestions</li>
              <li style={{ marginBottom: '0.5rem' }}>Automated confirmations and notifications</li>
              <li style={{ marginBottom: '0.5rem' }}>Integration with patient and doctor Google Calendars</li>
              <li style={{ marginBottom: '0.5rem' }}>AI-driven natural language interaction</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Solution Architecture
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              We built an AI-driven scheduling agent orchestrated by n8n. The workflow:
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              1. Website Integration
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Patient booking forms on the website send requests to n8n.
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              2. AI Agent Scheduling
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              An LLM-based agent processes the request, queries doctor schedules, and evaluates slot availability.
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              3. Conflict Resolution
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              If the requested doctor or time slot is unavailable, the AI agent generates alternatives (different doctors or times).
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              4. Confirmation Workflow
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              Once the patient confirms, the system:
            </p>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Sends email confirmation via SMTP integration</li>
              <li style={{ marginBottom: '0.5rem' }}>Creates calendar events in both patient's and doctor's Google Calendars via Google Calendar API</li>
              <li style={{ marginBottom: '0.5rem' }}>Logs the appointment in the CRM for reporting</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Technologies Used
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>n8n for workflow automation</li>
              <li style={{ marginBottom: '0.5rem' }}>LLM API (OpenAI / Claude) for conflict resolution and conversational interaction</li>
              <li style={{ marginBottom: '0.5rem' }}>Google Calendar API for event creation</li>
              <li style={{ marginBottom: '0.5rem' }}>SMTP (Gmail integration) for email confirmations</li>
              <li style={{ marginBottom: '0.5rem' }}>Webhook integration with the clinic's website</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Impact
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'rgba(230, 57, 70, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--primary-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>70%</h3>
                <p style={{ margin: 0 }}>Reduction in Scheduling Conflicts</p>
              </div>
              <div style={{
                background: 'rgba(0, 119, 182, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--accent-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>{'<1 min'}</h3>
                <p style={{ margin: 0 }}>Appointment Confirmation Time</p>
              </div>
            </div>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Improved doctor-patient communication and trust</li>
              <li style={{ marginBottom: '0.5rem' }}>Scalable system that can handle thousands of appointment requests monthly</li>
            </ul>

            <div style={{
              background: 'rgba(230, 57, 70, 0.1)',
              padding: '1.5rem',
              borderRadius: '10px',
              borderLeft: '4px solid var(--primary-color)',
              marginTop: '2rem'
            }}>
              <p style={{ margin: 0, fontStyle: 'italic' }}>
                Ready to streamline your healthcare operations? Contact Aakeeo today to discover how our AI-powered automation solutions can transform your appointment scheduling and improve patient satisfaction.
              </p>
            </div>
          </div>
        </div>
      </article>
        <BackToTop />
    </div>
  );
};

export default HealthcareScheduling;